import { zodResolver } from "@hookform/resolvers/zod";
import {
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Result,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { adminApi } from "@/api/client";
import { requestMfaTicket } from "@/api/mfa";
import { MonospaceCell } from "@/components/MonospaceCell";
import { usePermissionsQuery } from "@/hooks/useAdminQueries";

const BanSchema = z.object({
  reason: z
    .string()
    .trim()
    .refine((s) => s.length >= 10, { message: "min" }),
});

const NoteSchema = z.object({
  body: z.string().trim().min(1),
});

const MfaDemoSchema = z.object({
  totp: z.string().trim().regex(/^\d{6}$/),
});

type DetailRow = Record<string, unknown>;

export function UserDetailPage() {
  const { userId } = useParams();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { data: permissions = [] } = usePermissionsQuery(true);
  const canWrite = permissions.includes("*");

  const detail = useQuery({
    queryKey: ["admin", "users", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, response } = await adminApi.GET(
        "/admin/v1/users/{user_id}",
        {
          params: { path: { user_id: userId as string } },
        }
      );
      if (!response.ok || !data) throw new Error("user_detail");
      return data as DetailRow;
    },
  });

  const tags = (detail.data?.tags as string[] | undefined) ?? [];
  const isBanned = tags.includes("blacklist");

  const {
    control: banControl,
    handleSubmit: handleBanSubmit,
    reset: resetBan,
    formState: banFormState,
  } = useForm({
    resolver: zodResolver(BanSchema),
    defaultValues: { reason: "" },
  });

  const {
    control: noteControl,
    handleSubmit: handleNoteSubmit,
    reset: resetNote,
  } = useForm({
    resolver: zodResolver(NoteSchema),
    defaultValues: { body: "" },
  });

  const {
    control: mfaControl,
    handleSubmit: handleMfaSubmit,
    reset: resetMfa,
  } = useForm({
    resolver: zodResolver(MfaDemoSchema),
    defaultValues: { totp: "" },
  });

  const banMut = useMutation({
    mutationFn: async (reason: string) => {
      const { response } = await adminApi.POST(
        "/admin/v1/users/{user_id}/ban",
        {
          params: { path: { user_id: userId as string } },
          body: { reason },
        }
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || String(response.status));
      }
    },
    onSuccess: async () => {
      message.success(t("users.banOk"));
      resetBan({ reason: "" });
      await qc.invalidateQueries({ queryKey: ["admin", "users", userId] });
    },
    onError: () => message.error(t("users.banFail")),
  });

  const unbanMut = useMutation({
    mutationFn: async () => {
      const { response } = await adminApi.POST(
        "/admin/v1/users/{user_id}/unban",
        { params: { path: { user_id: userId as string } } }
      );
      if (!response.ok) throw new Error(String(response.status));
    },
    onSuccess: async () => {
      message.success(t("users.unbanOk"));
      await qc.invalidateQueries({ queryKey: ["admin", "users", userId] });
    },
    onError: () => message.error(t("users.unbanFail")),
  });

  const noteMut = useMutation({
    mutationFn: async (body: string) => {
      const { response } = await adminApi.POST(
        "/admin/v1/users/{user_id}/internal-notes",
        {
          params: { path: { user_id: userId as string } },
          body: { body },
        }
      );
      if (!response.ok) throw new Error(String(response.status));
    },
    onSuccess: async () => {
      message.success(t("users.noteOk"));
      resetNote({ body: "" });
      await qc.invalidateQueries({ queryKey: ["admin", "users", userId] });
    },
    onError: () => message.error(t("users.noteFail")),
  });

  const onBan = handleBanSubmit((vals) => {
    Modal.confirm({
      title: t("users.banConfirmTitle"),
      content: t("users.banConfirmBody"),
      okType: "danger",
      onOk: () => banMut.mutateAsync(vals.reason),
    });
  });

  const onUnban = () => {
    Modal.confirm({
      title: t("users.unbanConfirmTitle"),
      onOk: () => unbanMut.mutateAsync(),
    });
  };

  const onNote = handleNoteSubmit((vals) => noteMut.mutateAsync(vals.body));

  const onMfaDemo = handleMfaSubmit(async (vals) => {
    try {
      const ticket = await requestMfaTicket(vals.totp);
      message.success(
        t("users.mfaTicketOk", { hint: `${ticket.slice(0, 10)}…` })
      );
      resetMfa({ totp: "" });
    } catch {
      message.error(t("users.mfaTicketFail"));
    }
  });

  if (!userId)
    return <Result status="404" title={t("users.detailMissingId")} />;

  if (detail.isError) {
    return (
      <Result
        status="error"
        title={t("users.error")}
        subTitle={detail.error?.message}
        extra={
          <Button type="primary" onClick={() => detail.refetch()}>
            {t("common.retry")}
          </Button>
        }
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={<MonospaceCell>{userId}</MonospaceCell>}>
        {detail.isPending ? (
          <Skeleton active />
        ) : (
          <>
            <Space style={{ marginBottom: 16 }} wrap>
              {isBanned ? (
                <Tag color="error">{t("users.tagBanned")}</Tag>
              ) : (
                <Tag color="success">{t("users.tagActive")}</Tag>
              )}
              {tags
                .filter((x) => x !== "blacklist")
                .map((k) => (
                  <Tag key={k}>
                    <MonospaceCell>{k}</MonospaceCell>
                  </Tag>
                ))}
            </Space>
            <Descriptions bordered column={{ xs: 1, lg: 2 }}>
              {Object.entries(detail.data ?? {})
                .filter(([k]) => k !== "tags")
                .slice(0, 24)
                .map(([k, v]) => (
                  <Descriptions.Item key={k} label={k}>
                    {typeof v === "object"
                      ? JSON.stringify(v)
                      : String(v ?? "—")}
                  </Descriptions.Item>
                ))}
            </Descriptions>
          </>
        )}
      </Card>

      {canWrite ? (
        <Card title={t("users.opsTitle")}>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {isBanned ? (
              <Button onClick={onUnban} loading={unbanMut.isPending}>
                {t("users.unban")}
              </Button>
            ) : (
              <Form
                layout="vertical"
                onFinish={() => void onBan()}
                style={{ maxWidth: 480 }}
              >
                <Typography.Paragraph type="secondary">
                  {t("users.banHint")}
                </Typography.Paragraph>
                <Form.Item
                  label={t("users.banReason")}
                  required
                  validateStatus={banFormState.errors.reason ? "error" : undefined}
                  help={
                    banFormState.errors.reason ? t("users.reasonMin") : undefined
                  }
                >
                  <Controller
                    name="reason"
                    control={banControl}
                    render={({ field, fieldState }) => (
                      <Input.TextArea
                        {...field}
                        rows={3}
                        status={fieldState.error ? "error" : undefined}
                      />
                    )}
                  />
                </Form.Item>
                <Button
                  type="primary"
                  danger
                  htmlType="submit"
                  loading={banMut.isPending}
                >
                  {t("users.banSubmit")}
                </Button>
              </Form>
            )}
          </Space>
        </Card>
      ) : null}

      {canWrite ? (
        <Card title={t("users.internalNoteTitle")}>
        <Typography.Paragraph type="secondary">
          {t("users.internalNoteHint")}
        </Typography.Paragraph>
        <Form
          layout="vertical"
          onFinish={() => void onNote()}
          style={{ maxWidth: 480 }}
        >
          <Form.Item label={t("users.internalNoteBody")} required>
            <Controller
              name="body"
              control={noteControl}
              render={({ field, fieldState }) => (
                <Input.TextArea
                  {...field}
                  rows={3}
                  status={fieldState.error ? "error" : undefined}
                />
              )}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={noteMut.isPending}>
            {t("users.internalNoteSubmit")}
          </Button>
        </Form>
      </Card>
      ) : null}

      <Card title={t("users.mfaDemoTitle")}>
        <Typography.Paragraph>{t("users.mfaDemoHint")}</Typography.Paragraph>
        <Form layout="inline" onFinish={() => void onMfaDemo()}>
          <Form.Item label={t("users.mfaTotp")}>
            <Controller
              name="totp"
              control={mfaControl}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  status={fieldState.error ? "error" : undefined}
                  placeholder="000000"
                />
              )}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit">
            {t("users.mfaChallenge")}
          </Button>
        </Form>
      </Card>
    </Space>
  );
}
